import {
  createStore, applyMiddleware, combineReducers, Reducer, Store, Action
} from 'redux';
import { thunk } from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import { reducersConfiguration } from 'dependencies/reducers';
import { STATE } from 'app/constants';
import { isDevelopment } from './universal';
import { log } from './log';
import { typedToPlain } from './redux';
import { textCollectionReducer } from 'app/infrastructure/translate';
import { errorsReducer } from 'app/shell/reducers';
import { crashNotification } from 'app/infrastructure/notifications/crash-notification';
import { logErrors, createSaveMiddleware } from 'app/infrastructure/redux';
import { actionCreators as draftActions } from 'app/area/case/draft/actions';
import { userMessages, reducer as messageReducer } from 'app/infrastructure/notifications/user-messages';
import { ApplicationState } from 'app/types-store';

export function configureStore(): Store<ApplicationState, Action> {

  const enabledMiddleware = [
    thunk,
    logErrors,
    typedToPlain,
    userMessages,
    createSaveMiddleware(draftActions.backgroundSave, draftActions.submitDocument, STATE.case)
  ];

  if (isDevelopment) {
    log('Development mode');
    enabledMiddleware.push(require('redux-freeze') as any);
  }

  const middlewares = applyMiddleware(...enabledMiddleware);

  const composer = composeWithDevTools(middlewares);

  const createStoreWithMiddleware = composer(createStore);

  const { INITIAL_APP_STATE, reducers } = reducersConfiguration;

  // Combine all reducers and instantiate the app-wide store instance
  const allReducers: Reducer<any> = buildRootReducer(reducers);

  const store = createStoreWithMiddleware(allReducers, INITIAL_APP_STATE);

  // Enable Webpack hot module replacement for reducers
  if (module.hot) {
    module.hot.accept('app/types-store', () => {
      const factory = require('app/factory-default');
      store.replaceReducer(buildRootReducer(factory.reducers));
    });
  }

  return store;
}

export const store = configureStore();

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>

export type AppDispatch = typeof store.dispatch

/**
 * Runs both default reducers, and reducers that operate on full state, for cross-cutting concerns
 */
export function buildRootReducer(allReducers: any) {

  return combineSliceAndGlobalReducers(
    {
      ...allReducers
    },
    [textCollectionReducer, errorsReducer, messageReducer]
  );
}

/**
 * Creates one function composed by default redux reducers that access their own slice of state, and a set of
 * reducers that have access to the full store (ApplicationState)
 */
function combineSliceAndGlobalReducers(keyedReducers: any = {}, reducers: Reducer[]): Reducer {

  return (state = {}, action: any) => {

    let result: any = Object.assign({}, state);

    try {
      // assert for any errors, but don't use the result
      if (isDevelopment) {
        combineReducers(keyedReducers);
      }
      // run reducers that are specific to top-level keys
      Object.keys(keyedReducers).reduce((prev: any, key: any) => {
        const reducer = keyedReducers[key];
        result[key] = reducer(result[key], action);
      }, result);

      // run reducers that access the complete state
      reducers.reduce((prev, reducer) => {
        result = reducer(result, action);
      }, result);

    } catch (err) {
      log('A reducer crashed, entering fatal state');
      console.error(err);

      crashNotification('We are sorry! Synergi Life has stopped working.', err.message);
    }

    return result;
  };
}

export function getReselectCheckOptions(): { inputStabilityCheck: 'once' | 'never'; identityFunctionCheck: 'once' | 'never' } {
  const option = isDevelopment ? 'once' : 'never';
  return { inputStabilityCheck: option, identityFunctionCheck: option };
}
